<template>
  <div id="app" class="flex items-center justify-center w-screen h-screen relative" :class="$route.name">
    <div class="absolute inset-0 overlay" />
    <div class="relative w-full h-full sm:overflow-hidden app-container">
      <!-- if cookies are disabled show message -->
      <div v-if="cookiesBlocked" class="p-3 bg-grey-100 quote-app-view ">
        <div class="relative h-full w-full flex items-center justify-center">
          <DrIcon
            svg="delete-circle"
            width="24"
            height="24"
            class="absolute top-0 right-0 cursor-pointer"
            @click.native="closeWidget('')"
          />
          <div class="text-center">
            <DrIcon svg="alert" width="40" height="40" />
            <div>
              Whoops! You need to update your cookie settings to fully access our website.
            </div>
          </div>
        </div>
      </div>
      <div
        v-else-if="$store.state.portal && portalWarning"
        class="quote-app-view bg-red-500 flex-center flex-col relative h-full"
      >
        <DrIcon
          svg="delete-circle"
          width="24"
          height="24"
          class="absolute text-white top-0 right-0 cursor-pointer mr-2 mt-3"
          @click.native="closeWidget('')"
        />
        <p class="text-white text-xl leading-snug font-semibold max-w-lg text-center px-4">
          Please make sure your business details are up to date before adding any cover
        </p>

        <div class="mt-12 bg-white rounded px-4 py-6 sm:px-8 sm:py-12 mx-2 sm:max-w-lg h-48 flex-center" style="width: 95vw;">
          <DrLoader v-if="!portalReady" />
          <div v-else class="w-full">
            <div class="flex items-center mt-0">
              <p class="font-semibold w-1/2 mb-0 flex-shrink-0">Legal business name</p>
              <p class="text-grey-500 ml-3 mb-0 w-full truncate">{{ $store.state.contact.businessName }}</p>
            </div>
            <div class="flex items-center mt-2">
              <p class="font-semibold w-1/2 mb-0 flex-shrink-0">Annual turnover</p>
              <p class="text-grey-500 ml-3 mb-0 w-full">
                <span v-if="$store.state.contact.turnover">{{ $store.state.contact.turnover | currency }}</span>
                <span v-else>Missing</span>
              </p>
            </div>
            <div class="flex items-center mt-2">
              <p class="font-semibold w-1/2 mb-0 flex-shrink-0">Number of employees</p>
              <p class="text-grey-500 ml-3 mb-0 w-full">
                <span v-if="$store.state.contact.employeeCount">Up to {{ $store.state.contact.employeeCount }}</span>
                <span v-else>Missing</span>
              </p>
            </div>
            <div v-if="$store.state.contact.tradingName" class="flex items-center mt-2">
              <p class="font-semibold w-1/2 mb-0 flex-shrink-0">Trading name</p>
              <p class="text-grey-500 ml-3 mb-0 w-full">{{ $store.state.contact.tradingName }}</p>
            </div>
          </div>
        </div>

        <transition name="fade" mode="out-in">
          <div v-if="portalReady && $store.state.contact.employeeCount && $store.state.contact.turnover" key="continue" class="mt-16 flex flex-col">
            <DrButton class="btn-cyan sm:max-w-sm sm:w-screen rounded-full font-semibold" @click="portalWarning = false">
              Continue
            </DrButton>
            <DrButton
              class="mt-1 btn-transparent text-white font-semibold rounded-full self-center"
              @click="closeWidget('business-details')"
            >
              Or edit business details
            </DrButton>
          </div>

          <div v-else-if="portalReady" key="missing" class="mt-16 flex flex-col">
            <p class="max-w-sm text-center text-white">
              Employee count or annual turnover missing, please update your business details
            </p>
            <DrButton
              class="mt-1 btn-cyan rounded-full font-semibold self-center"
              @click="closeWidget('business-details')"
            >
              Update business details
            </DrButton>
          </div>
        </transition>
      </div>
      <!-- end disabled cookie message -->
      <div v-else class="h-full sm:overflow-hidden relative quote-app-view">
        <div v-if="!storeReady" />
        <div v-else class="h-full w-full">
          <Transition name="delay-out">
            <Hero v-if="!isProductInfoPage() && !isLoginPage()" transition-titles class="fixed-hero" />
          </Transition>
          <Transition name="delay-out">
            <FixedFooter
              v-if="
                $route.path === '/products' ||
                  $route.path === '/products/start' ||
                  $route.name === 'product-config' ||
                  $route.path === '/overview' ||
                  $route.path === '/quote-confirmation'
              "
            />
          </Transition>
          <PortalTarget class="z-10 absolute top-0 left-0 h-full w-full" name="product-slideshow" slim />
          <div class="h-full relative z-10 m-auto bg-grey-100 app-view">
            <div
              class="relative bg-grey-100 z-0 main-view h-screen overflow-y-scroll scroll-view  overflow-scrolling-touch"
            >
              <Transition :name="transitionName" :mode="transitionMode">
                <RouterView
                  v-if="!$store.state.portal || ($store.state.portal && portalReady)"
                  class="relative w-full bg-grey-100"
                />
              </Transition>
            </div>
          </div>
        </div>
      </div>
      <div class="hidden iframe-footer">
        <div class="flex justify-between items-center text-grey-400">
          <div class="logo flex items-start">
            <span class="mr-1 text-3xs" style="line-height: 14px;">Powered by</span>
            <DrIcon svg="digital-risks-logo" scale class="dr-logo" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import FixedFooter from '@/components/footer/FixedFooter.vue'

export default {
  components: {
    FixedFooter,
  },
  data () {
    return {
      transitionName: '',
      transitionMode: '',
      cookiesBlocked: false,
      portalWarning: true,
    }
  },
  computed: {
    storeReady () {
      return this.$store.getters.getStoreReady
    },
    portalReady () {
      return this.$store.getters.getPortalReady
    },
  },
  watch: {
    $route (to, from) {
      this.transitionMode = '' // set the default mode

      if (!from.meta.page) {
        this.transitionName = ''
      } else if (to.path.endsWith('info')) {
        this.transitionName = 'delay-out'
        this.transitionMode = 'in-out'
      } else {
        this.transitionName = to.meta.page > from.meta.page ? 'slide-next' : 'slide-prev'
      }
    },
  },
  mounted () {
    const test = 'test'
    try {
      localStorage.setItem(test, test)
      localStorage.removeItem(test)
      this.cookiesBlocked = false
    } catch (e) {
      this.cookiesBlocked = true
    }
  },
  methods: {
    isProductConfigPage () {
      return this.$route.path.startsWith('/products')
    },
    isProductInfoPage () {
      return (
        this.$route.path.endsWith('/info') ||
        this.$route.path === '/success' ||
        this.$route.path === '/404' ||
        this.$route.path === '/refer-quote' ||
        this.$route.path === '/sanctions-search-failure'
      )
    },
    isLoginPage () {
      if (!this.$route.name) return false
      return this.$route.name.startsWith('user-')
    },
    closeWidget (route) {
      const payload = { from: 'dr-widget' }
      if (route) payload.redirect = route
      window.parent.postMessage({ type: 'close-iframe', payload }, '*')
    },
  },
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css?family=Open+Sans:400,400i,600,700&display=swap');

.overlay {
  background-color: rgba(5, 21, 38, 0.9);
}

.overflow-scrolling-touch {
  -webkit-overflow-scrolling: touch;
}

.main-view {
  display: grid;
  grid-template: 'main';
}

.iframe-footer {
  line-height: 16px;
}

@media (min-width: 660px) {
  .app-container {
    max-width: 660px;
    max-height: 707px;
  }

  .quote-app-view {
    height: calc(100% - 28px); /* 28px is height of Digital Risks footer */
    border-radius: 4px;
  }

  .main-view {
    @apply h-full;
  }

  .scroll-view {
    max-height: 679px; /* height of widget - height of Digital Risks footer */
  }

  .iframe-footer {
    @apply block pt-3 bg-transparent;
  }
}

.main-view > :first-child {
  z-index: 1; /* Prevent flickering on first frame when transition classes not added yet */
}

.main-view > :not(:first-child) {
  z-index: 0; /* Prevent flickering on first frame when transition classes not added yet */
}

.main-view > * {
  position: relative;
  grid-area: main; /* Transition: make sections overlap on same cell */
}

.dr-logo {
  width: 85px;
  opacity: 0.95;

  @apply pt-px ml-px mt-px;
}
</style>
